// shell/components/grid/grid-renderer.ts

import { ColumnDef, RenderedCell } from '../table/types';
import { ColumnManager } from '../table/column-manager';
import { ColumnRendererFactory } from '../table/column-renderer-factory';
import { CellRenderContext, CellUpdateType } from '../table/columns/column-base';
import { HeatMapBounds } from '../heat-map';
import { RowState, IDisposable } from '../types';
import { IRenderer } from '../renderer-types';
import {
    AnimationTimeoutManager,
    EventListenerTracker,
    computeShadeRank,
    safeGetValue,
    findInsertionIndex,
    FRESH_HIGHLIGHT_DURATION_MS
} from '../renderer-utils';

/**
 * Rendered card tracking for updates.
 */
interface RenderedCard<T> {
    /** The card element */
    element: HTMLElement;
    /** Card index (mutable - updated on recycle) */
    index: number;
    /** Item at render time (mutable - updated on recycle) */
    item: T;
    /** Rendered cells for each column */
    cells: Map<string, RenderedCell<T>>;
    /** Selection checkbox (if selectable) */
    checkboxElement?: HTMLElement;
}

/**
 * Configuration for GridRenderer.
 */
export interface GridRendererConfig<T> {
    /** Column manager instance */
    columnManager: ColumnManager<T>;

    /** Column renderer factory (owns shared HeatMapShader) */
    rendererFactory: ColumnRendererFactory<T>;

    /** Extract card ID from item */
    getCardId: (item: T) => string;

    /** Get visual state for card */
    getCardState?: (item: T) => RowState | undefined;

    /** Custom CSS class for card */
    getCardClass?: (item: T, index: number) => string | undefined;

    /** Card height in pixels for virtualization (default: 200) */
    cardHeight?: number;

    /** Minimum card width in pixels for column calculation (default: 200) */
    minCardWidth?: number;

    /** Gap between cards in pixels (default: 8) */
    gap?: number;

    /** Total item count for ARIA */
    totalCount?: number;

    /** Duration for fresh highlight animation in ms (default: 2000) */
    freshHighlightDuration?: number;

    /** CSS grid template for card layout (optional - uses default if not provided) */
    cardGridTemplate?: string;

    /** CSS grid-template-areas for named grid areas (optional) */
    cardGridAreas?: string;

    // === Events ===

    /** Card clicked */
    onCardClick?: (item: T, index: number, event: MouseEvent) => void;

    /** Card double-clicked */
    onCardDoubleClick?: (item: T, index: number, event: MouseEvent) => void;

    /** Cell clicked within card */
    onCellClick?: (item: T, columnKey: string, event: MouseEvent) => void;

    /** Keyboard event on focused card */
    onKeyDown?: (event: KeyboardEvent, focusedItem: T | null) => boolean;

    // === Selection integration ===

    /** Whether selection is enabled */
    selectable?: boolean;

    /** Check if item is selected (checkbox state for bulk actions) */
    isSelected?: (item: T) => boolean;

    /** Check if item is the active/focused card (master-detail highlight) */
    isActive?: (item: T) => boolean;

    /** Toggle selection on item */
    onSelectionToggle?: (item: T, event: MouseEvent) => void;

    // === Card preprocessing ===

    /** Called before card is rendered */
    onCardRender?: (element: HTMLElement, item: T, index: number) => void;

    /** Called after card is rendered */
    onCardRendered?: (element: HTMLElement, item: T, index: number) => void;
}

/**
 * GridRenderer creates card elements for visible items.
 *
 * Key responsibilities:
 * - Build card DOM elements with cells in grid layout
 * - Delegate cell rendering to ColumnRendererFactory
 * - Handle selection checkbox overlay
 * - Apply card state CSS classes
 * - Emit events for card/cell interactions
 * - Keyboard navigation between cards
 *
 * Does NOT handle:
 * - Scroll position or virtualization (VirtualScroller's job)
 * - Data fetching (DataSource's job)
 * - Selection state tracking (SelectionManager's job)
 */
export class GridRenderer<T> implements IRenderer<T> {
    private readonly config: GridRendererConfig<T>;
    private readonly columnManager: ColumnManager<T>;
    private readonly rendererFactory: ColumnRendererFactory<T>;
    private readonly timeoutManager = new AnimationTimeoutManager();
    private readonly eventTracker = new EventListenerTracker();
    private readonly freshDuration: number;

    private container: HTMLElement | null = null;
    private renderedCards = new Map<number, RenderedCard<T>>();
    private focusedIndex = -1;
    private cardHeight: number;
    private minCardWidth: number;
    private gap: number;
    private totalCount = 0;
    private selectable: boolean;

    // Cached per render batch
    private cachedColumns: ColumnDef<T>[] | null = null;
    private cachedHeatMapBounds: Map<string, HeatMapBounds> | null = null;

    constructor(config: GridRendererConfig<T>) {
        this.config = config;
        this.columnManager = config.columnManager;
        this.rendererFactory = config.rendererFactory;
        this.cardHeight = config.cardHeight ?? 200;
        this.minCardWidth = config.minCardWidth ?? 200;
        this.gap = config.gap ?? 8;
        this.totalCount = config.totalCount ?? 0;
        this.selectable = config.selectable ?? false;
        this.freshDuration = config.freshHighlightDuration ?? FRESH_HIGHLIGHT_DURATION_MS;
    }

    render(container: HTMLElement, items: T[], startIndex: number): void {
        this.container = container;

        // Cache columns and heat map bounds for this render batch
        this.cachedColumns = this.columnManager.getVisibleColumns();
        this.cachedHeatMapBounds = this.getHeatMapBounds(this.cachedColumns);

        // Determine which cards need to be added/removed
        const newIndices = new Set<number>();
        for (let i = 0; i < items.length; i++) {
            if (items[i] != null) {
                newIndices.add(startIndex + i);
            }
        }
        const existingIndices = new Set(this.renderedCards.keys());

        // Remove cards no longer in range
        for (const index of existingIndices) {
            if (!newIndices.has(index)) {
                const card = this.renderedCards.get(index)!;
                this.cleanupCard(card);
                card.element.remove();
                this.renderedCards.delete(index);
                this.timeoutManager.cancel(`fresh-${index}`);
            }
        }

        // Collect new cards to add
        const newCards: Array<{ card: RenderedCard<T>; index: number }> = [];

        // Add/update cards
        for (let i = 0; i < items.length; i++) {
            const index = startIndex + i;
            const item = items[i];

            if (item == null) continue;

            if (this.renderedCards.has(index)) {
                // Update existing card
                this.updateCardElement(this.renderedCards.get(index)!, item, index);
            } else {
                // Create new card
                const card = this.createCardElement(item, index);
                newCards.push({ card, index });
            }
        }

        // Sort new cards by index
        newCards.sort((a, b) => a.index - b.index);

        // Insert new cards
        if (newCards.length > 0) {
            this.insertCardsBatched(newCards);
        }

        // Clear cache
        this.cachedColumns = null;
        this.cachedHeatMapBounds = null;
    }

    private insertCardsBatched(
        newCards: Array<{ card: RenderedCard<T>; index: number }>
    ): void {
        if (!this.container) return;

        const existingIndices = Array.from(this.renderedCards.keys()).sort((a, b) => a - b);

        let currentBatch: HTMLElement[] = [];
        let insertBeforeIndex: number | null = null;

        for (const { card, index } of newCards) {
            const targetInsertBefore = this.findInsertBeforeIndex(existingIndices, index);

            if (insertBeforeIndex === null) {
                insertBeforeIndex = targetInsertBefore;
            }

            if (targetInsertBefore === insertBeforeIndex) {
                currentBatch.push(card.element);
            } else {
                this.flushBatch(currentBatch, insertBeforeIndex);
                currentBatch = [card.element];
                insertBeforeIndex = targetInsertBefore;
            }

            this.renderedCards.set(index, card);
            existingIndices.splice(
                findInsertionIndex(existingIndices, index),
                0,
                index
            );
        }

        if (currentBatch.length > 0) {
            this.flushBatch(currentBatch, insertBeforeIndex);
        }
    }

    private findInsertBeforeIndex(sortedIndices: number[], targetIndex: number): number | null {
        const pos = findInsertionIndex(sortedIndices, targetIndex);
        return pos < sortedIndices.length ? sortedIndices[pos] : null;
    }

    private flushBatch(elements: HTMLElement[], insertBeforeIndex: number | null): void {
        if (!this.container || elements.length === 0) return;

        const fragment = document.createDocumentFragment();
        for (const el of elements) {
            fragment.appendChild(el);
        }

        if (insertBeforeIndex !== null) {
            const beforeCard = this.renderedCards.get(insertBeforeIndex);
            if (beforeCard) {
                this.container.insertBefore(fragment, beforeCard.element);
                return;
            }
        }

        this.container.appendChild(fragment);
    }

    private createCardElement(item: T, index: number): RenderedCard<T> {
        const card = document.createElement('div');
        card.className = 'cs-grid-card';
        card.setAttribute('role', 'listitem');
        card.setAttribute('data-index', String(index));
        card.setAttribute('data-id', this.config.getCardId(item));
        // Set visual row for VirtualScroller height tracking
        const columnsPerRow = this.getColumnsPerRow();
        card.setAttribute('data-virtual-row', String(Math.floor(index / columnsPerRow)));
        card.tabIndex = -1;

        // Create card object first for event handlers
        const renderedCard: RenderedCard<T> = {
            element: card,
            index,
            item,
            cells: new Map(),
            checkboxElement: undefined
        };

        // Apply card state
        this.applyCardState(card, item);

        // Custom class
        const customClass = this.config.getCardClass?.(item, index);
        if (customClass) card.classList.add(customClass);

        // Selection checkbox overlay (before custom render so it appears on top)
        if (this.selectable) {
            renderedCard.checkboxElement = this.addCheckboxOverlay(card, renderedCard);
        }

        // Custom card render - if provided, skip default column rendering
        if (this.config.onCardRender) {
            this.config.onCardRender(card, item, index);
            // Event handlers
            this.attachCardEvents(card, renderedCard);
            // Post-render hook
            this.config.onCardRendered?.(card, item, index);
            return renderedCard;
        }

        // Default column-based rendering
        // Create content container with grid layout
        const content = document.createElement('div');
        content.className = 'cs-grid-card__content';

        // Apply custom grid template if provided
        if (this.config.cardGridTemplate) {
            content.style.gridTemplateColumns = this.config.cardGridTemplate;
        }
        if (this.config.cardGridAreas) {
            content.style.gridTemplateAreas = this.config.cardGridAreas;
        }

        // Render cells for each visible column
        const columns = this.cachedColumns ?? this.columnManager.getVisibleColumns();
        const heatMapBoundsMap = this.cachedHeatMapBounds ?? this.getHeatMapBounds(columns);

        // Sort columns by grid order if specified
        const sortedColumns = [...columns].sort((a, b) => {
            const orderA = a.grid?.order ?? 0;
            const orderB = b.grid?.order ?? 0;
            return orderA - orderB;
        });

        for (const column of sortedColumns) {
            const cellWrapper = document.createElement('div');
            cellWrapper.className = 'cs-grid-cell';
            cellWrapper.setAttribute('data-column', column.key);

            // Apply grid positioning from column definition
            if (column.grid) {
                if (column.grid.row !== undefined) {
                    cellWrapper.style.gridRow = String(column.grid.row);
                }
                if (column.grid.column !== undefined) {
                    cellWrapper.style.gridColumn = String(column.grid.column);
                }
                if (column.grid.colSpan) {
                    cellWrapper.style.gridColumn = `span ${column.grid.colSpan}`;
                }
                if (column.grid.rowSpan) {
                    cellWrapper.style.gridRow = `span ${column.grid.rowSpan}`;
                }
                if (column.grid.area) {
                    cellWrapper.style.gridArea = column.grid.area;
                }
            }

            // Get value safely
            const value = safeGetValue(column, item);

            // Get heat map bounds and rank
            const bounds = heatMapBoundsMap.get(column.key);
            const shadeRank = computeShadeRank(value, bounds, column, item);

            const ctx: CellRenderContext<T> = {
                column,
                item,
                value,
                rowIndex: index,
                heatMapBounds: bounds,
                shadeRank
            };

            // Delegate to factory
            const renderedCell = this.rendererFactory.renderGridCell(
                column,
                ctx,
                cellWrapper
            );

            renderedCard.cells.set(column.key, renderedCell);

            // Cell click handler
            const cellClickHandler = (e: Event) => {
                this.config.onCellClick?.(renderedCard.item, column.key, e as MouseEvent);
            };
            cellWrapper.addEventListener('click', cellClickHandler);

            content.appendChild(cellWrapper);
        }

        card.appendChild(content);

        // Event handlers
        this.attachCardEvents(card, renderedCard);

        // Post-render hook
        this.config.onCardRendered?.(card, item, index);

        return renderedCard;
    }

    private updateCardElement(card: RenderedCard<T>, item: T, index: number): void {
        // Update mutable fields
        card.index = index;
        card.item = item;

        // Update data attributes
        card.element.setAttribute('data-index', String(index));
        card.element.setAttribute('data-id', this.config.getCardId(item));

        // Update card state
        this.applyCardState(card.element, item);

        // Update checkbox if present
        if (card.checkboxElement && this.selectable) {
            this.updateCheckbox(card.checkboxElement, item);
        }

        // Custom card render - re-render the card content
        if (this.config.onCardRender) {
            // Clear existing content (except checkbox overlay)
            const checkbox = card.checkboxElement;
            card.element.innerHTML = '';
            if (checkbox) {
                card.element.appendChild(checkbox);
            }
            // Re-render custom content
            this.config.onCardRender(card.element, item, index);
            return;
        }

        // Default column-based update
        const columns = this.cachedColumns ?? this.columnManager.getVisibleColumns();
        const heatMapBoundsMap = this.cachedHeatMapBounds ?? this.getHeatMapBounds(columns);

        for (const column of columns) {
            const renderedCell = card.cells.get(column.key);
            if (!renderedCell) {
                console.warn(`Column ${column.key} not found in rendered card. Call onColumnsChanged() when columns change.`);
                continue;
            }

            const value = safeGetValue(column, item);
            const bounds = heatMapBoundsMap.get(column.key);
            const shadeRank = computeShadeRank(value, bounds, column, item);

            const ctx: CellRenderContext<T> = {
                column,
                item,
                value,
                rowIndex: index,
                heatMapBounds: bounds,
                shadeRank
            };

            // Update via factory
            const updatedCell = this.rendererFactory.updateGridCell(
                renderedCell,
                ctx,
                CellUpdateType.Full
            );

            card.cells.set(column.key, updatedCell);
        }
    }

    private cleanupCard(card: RenderedCard<T>): void {
        for (const cell of card.cells.values()) {
            this.rendererFactory.cleanupCell(cell);
        }
        card.cells.clear();
    }

    private addCheckboxOverlay(card: HTMLElement, renderedCard: RenderedCard<T>): HTMLElement {
        const overlay = document.createElement('div');
        overlay.className = 'cs-grid-card__checkbox';

        const checkbox = document.createElement('button');
        checkbox.type = 'button';
        checkbox.className = 'cs-checkbox';
        checkbox.setAttribute('aria-checked', String(this.config.isSelected?.(renderedCard.item) ?? false));
        checkbox.setAttribute('aria-label', 'Select card');

        if (this.config.isSelected?.(renderedCard.item)) {
            checkbox.classList.add('selected');
        }

        const clickHandler = (e: Event) => {
            e.stopPropagation();
            this.config.onSelectionToggle?.(renderedCard.item, e as MouseEvent);
        };
        checkbox.addEventListener('click', clickHandler);

        overlay.appendChild(checkbox);
        card.appendChild(overlay);
        return overlay;
    }

    private updateCheckbox(overlay: HTMLElement, item: T): void {
        const checkbox = overlay.querySelector('.cs-checkbox') as HTMLButtonElement;
        if (!checkbox) return;

        const isSelected = this.config.isSelected?.(item) ?? false;
        checkbox.setAttribute('aria-checked', String(isSelected));
        checkbox.classList.toggle('selected', isSelected);
    }

    private attachCardEvents(card: HTMLElement, renderedCard: RenderedCard<T>): void {
        card.addEventListener('click', (e: Event) => {
            this.config.onCardClick?.(renderedCard.item, renderedCard.index, e as MouseEvent);
        });

        card.addEventListener('dblclick', (e: Event) => {
            this.config.onCardDoubleClick?.(renderedCard.item, renderedCard.index, e as MouseEvent);
        });

        card.addEventListener('keydown', (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            if (this.config.onKeyDown?.(keyEvent, renderedCard.item)) {
                return;
            }
            this.handleKeyDown(keyEvent);
        });

        card.addEventListener('focus', () => {
            this.focusedIndex = renderedCard.index;
        });
    }

    private applyCardState(card: HTMLElement, item: T): void {
        const state = this.config.getCardState?.(item);
        const currentFresh = card.classList.contains('fresh');
        const currentDeleted = card.classList.contains('deleted');
        const currentVisited = card.classList.contains('visited');
        const currentUnread = card.classList.contains('unread');

        const newFresh = state?.fresh ?? false;
        const newDeleted = state?.deleted ?? false;
        const newVisited = state?.visited ?? false;
        const newUnread = state?.unread ?? false;

        if (currentFresh !== newFresh) card.classList.toggle('fresh', newFresh);
        if (currentDeleted !== newDeleted) card.classList.toggle('deleted', newDeleted);
        if (currentVisited !== newVisited) card.classList.toggle('visited', newVisited);
        if (currentUnread !== newUnread) card.classList.toggle('unread', newUnread);

        // Custom classes
        const prevCustom = (card.dataset.rsClasses ?? '').split(' ').filter(Boolean);
        const newCustom = state?.classes;
        for (const cls of prevCustom) {
            if (!newCustom?.has(cls)) card.classList.remove(cls);
        }
        if (newCustom) {
            for (const cls of newCustom) card.classList.add(cls);
            card.dataset.rsClasses = Array.from(newCustom).join(' ');
        } else if (prevCustom.length > 0) {
            delete card.dataset.rsClasses;
        }

        // Active card state (master-detail highlight, separate from checkbox selection)
        if (this.config.isActive) {
            const isActive = this.config.isActive(item);
            const currentActive = card.classList.contains('selected');
            if (currentActive !== isActive) {
                card.classList.toggle('selected', isActive);
            }
        }
    }

    private getHeatMapBounds(columns: ColumnDef<T>[]): Map<string, HeatMapBounds> {
        const bounds = new Map<string, HeatMapBounds>();
        for (const col of columns) {
            if (col.heatMap?.enabled && col.grid?.heatMap !== false) {
                const b = this.columnManager.getHeatMapBounds(col.key);
                if (b) bounds.set(col.key, b);
            }
        }
        return bounds;
    }

    updateHeatMap(bounds: Map<string, HeatMapBounds>): void {
        for (const card of this.renderedCards.values()) {
            for (const [columnKey, renderedCell] of card.cells) {
                const column = this.columnManager.getColumnByKey(columnKey);
                if (!column?.heatMap?.enabled || column.grid?.heatMap === false) continue;

                const columnBounds = bounds.get(columnKey);
                if (!columnBounds) continue;

                const value = safeGetValue(column, card.item);
                const shadeRank = computeShadeRank(value, columnBounds, column, card.item);

                const ctx: CellRenderContext<T> = {
                    column,
                    item: card.item,
                    value,
                    rowIndex: card.index,
                    heatMapBounds: columnBounds,
                    shadeRank
                };

                const updatedCell = this.rendererFactory.updateGridCell(
                    renderedCell,
                    ctx,
                    CellUpdateType.Shades
                );

                card.cells.set(columnKey, updatedCell);
            }
        }
    }

    updateItem(index: number, item: T): void {
        const card = this.renderedCards.get(index);
        if (card) {
            this.updateCardElement(card, item, index);
        }
    }

    updateItems(updates: Array<{ index: number; item: T }>): void {
        this.cachedColumns = this.columnManager.getVisibleColumns();
        this.cachedHeatMapBounds = this.getHeatMapBounds(this.cachedColumns);

        for (const { index, item } of updates) {
            const card = this.renderedCards.get(index);
            if (card) {
                this.updateCardElement(card, item, index);
            }
        }

        this.cachedColumns = null;
        this.cachedHeatMapBounds = null;
    }

    markItemDeleted(index: number): void {
        const card = this.renderedCards.get(index);
        if (card) {
            card.element.classList.add('deleted');
        }
    }

    markItemFresh(index: number): void {
        const card = this.renderedCards.get(index);
        if (card) {
            card.element.classList.add('fresh');
            this.timeoutManager.schedule(`fresh-${index}`, () => {
                const currentCard = this.renderedCards.get(index);
                if (currentCard && currentCard.element === card.element) {
                    card.element.classList.remove('fresh');
                }
            }, this.freshDuration);
        }
    }

    setSelectable(selectable: boolean): void {
        if (this.selectable === selectable) return;
        this.selectable = selectable;

        // Must re-render to add/remove checkbox overlays
        for (const card of this.renderedCards.values()) {
            this.cleanupCard(card);
            card.element.remove();
        }
        this.renderedCards.clear();
    }

    setTotalCount(count: number): void {
        this.totalCount = count;
        if (this.container) {
            this.container.setAttribute('aria-rowcount', String(count));
        }
    }

    onColumnsChanged(): void {
        // Clear all rendered cards and re-render
        for (const card of this.renderedCards.values()) {
            this.cleanupCard(card);
            card.element.remove();
        }
        this.renderedCards.clear();
        this.timeoutManager.cancelAll();
    }

    invalidate(): void {
        for (const card of this.renderedCards.values()) {
            this.cleanupCard(card);
            card.element.remove();
        }
        this.renderedCards.clear();
        this.timeoutManager.cancelAll();
    }

    focusItem(index: number): void {
        const card = this.renderedCards.get(index);
        if (card) {
            card.element.focus();
            this.focusedIndex = index;
        }
    }

    getFocusedIndex(): number {
        return this.focusedIndex;
    }

    handleKeyDown(event: KeyboardEvent): boolean {
        const currentIndex = this.focusedIndex;
        if (currentIndex < 0) return false;

        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const nextIndex = currentIndex + 1;
                if (this.renderedCards.has(nextIndex)) {
                    this.focusItem(nextIndex);
                    return true;
                }
                return false;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prevIndex = currentIndex - 1;
                if (prevIndex >= 0 && this.renderedCards.has(prevIndex)) {
                    this.focusItem(prevIndex);
                    return true;
                }
                return false;
            }
            case 'Home': {
                if (event.ctrlKey) {
                    event.preventDefault();
                    const minIndex = Math.min(...this.renderedCards.keys());
                    if (minIndex !== Infinity) {
                        this.focusItem(minIndex);
                        return true;
                    }
                }
                return false;
            }
            case 'End': {
                if (event.ctrlKey) {
                    event.preventDefault();
                    const maxIndex = Math.max(...this.renderedCards.keys());
                    if (maxIndex !== -Infinity) {
                        this.focusItem(maxIndex);
                        return true;
                    }
                }
                return false;
            }
            case 'Enter': {
                event.preventDefault();
                const card = this.renderedCards.get(currentIndex);
                if (card) {
                    card.element.click();
                }
                return true;
            }
            case ' ': {
                event.preventDefault();
                const card = this.renderedCards.get(currentIndex);
                if (card && this.selectable) {
                    this.config.onSelectionToggle?.(card.item, new MouseEvent('click'));
                }
                return true;
            }
        }

        return false;
    }

    getItemElement(index: number): HTMLElement | null {
        return this.renderedCards.get(index)?.element ?? null;
    }

    getRenderedElements(): HTMLElement[] {
        return Array.from(this.renderedCards.values()).map(c => c.element);
    }

    /**
     * Get the card height for virtualization.
     */
    getCardHeight(): number {
        return this.cardHeight;
    }

    /**
     * Set the card size dynamically (e.g., for thumbs mode slider).
     * Triggers a full re-render to apply the new size.
     */
    setCardSize(size: number): void {
        if (this.cardHeight === size) return;
        this.cardHeight = size;
        // Invalidate to force re-render with new size
        this.invalidate();
    }

    /**
     * Calculate number of columns that fit in current container width.
     * Used by DataListView for VirtualScroller configuration.
     */
    getColumnsPerRow(): number {
        if (!this.container) return 1;
        const containerWidth = this.container.clientWidth;
        if (containerWidth <= 0) return 1;
        // Calculate columns: (containerWidth + gap) / (cardWidth + gap)
        return Math.max(1, Math.floor((containerWidth + this.gap) / (this.minCardWidth + this.gap)));
    }

    dispose(): void {
        this.timeoutManager.dispose();
        this.eventTracker.dispose();

        for (const card of this.renderedCards.values()) {
            this.cleanupCard(card);
        }
        this.renderedCards.clear();
    }
}
